import { Navigate, Outlet, useLocation } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { Button, DialogError, DialogLoading } from "~/components";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentSession } from "~/api/sessions/hooks/use-session";
import { useDiningOption } from "~/providers/store/basket/hooks/use-basket";
import {
  useEmployee,
  useIsEmployeeAuthenticated
} from "~/providers/store/employee";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { BasketProvider } from "~/providers/store/basket/context";

export const PosLayout = () => {
  const { pathname } = useLocation();
  const { groupName } = useEmployee();
  const diningOption = useDiningOption();
  const t = useT();

  const {
    data: till,
    status: tillStatus,
    error: tillError,
    refetch: refetchTill
  } = useCurrentTill();
  const {
    status: sessionStatus,
    error: sessionError,
    refetch: refetchSession
  } = useCurrentSession();

  const isUserAuthenticated = useIsEmployeeAuthenticated();

  if (
    tillStatus === "pending" ||
    (till?.sessionId && sessionStatus === "pending")
  ) {
    return <DialogLoading title={t("Loading till and session")} />;
  }

  if (tillError || sessionError) {
    return (
      <DialogError
        error={
          tillError?.message ??
          sessionError?.message ??
          t("There was an unknown error loading till and session")
        }
      >
        <Button
          onClick={() => {
            void refetchTill();
            void refetchSession();
          }}
        >
          <T _str="Try again" />
        </Button>
      </DialogError>
    );
  }

  if (
    pathname !== "/pos/employee-card" &&
    till.type === "self-service" &&
    till.config.hasEmployeeCardReader &&
    groupName === null &&
    !isUserAuthenticated
  ) {
    return <Navigate replace to="/pos/employee-card" />;
  }

  if (
    pathname !== "/pos/selectors" &&
    till.type === "self-service" &&
    till.config.selectorSetup === "show-selectors" &&
    !till.config.hasEmployeeCardReader &&
    groupName === null &&
    !isUserAuthenticated
  ) {
    return <Navigate replace to="/pos/selectors" />;
  }

  const fields = till?.preCheckoutTextInputs ?? [];
  const firstField = fields.at(0);
  const options = firstField?.type === "select" ? firstField.options : [];
  // if there are text inputs and if there are options inside of them, redirect to dining options page
  const hasDiningOptions = Boolean(fields.length && options.length);
  if (
    pathname !== "/pos/dining-options" &&
    hasDiningOptions &&
    diningOption === null &&
    ((till.type === "self-service" &&
      (groupName !== null || till.config.selectorSetup !== "show-selectors")) ||
      hasAttendedSellConfig(till))
  ) {
    return <Navigate replace to="/pos/dining-options" />;
  }

  return (
    <BasketProvider>
      <Outlet />
    </BasketProvider>
  );
};
